import * as Sentry from '@sentry/react'
import {
	createContext,
	type ReactNode,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react'
import { getCurrentUserInfo, logout as logoutAPI } from '@/client/sdk.gen'
import type { UserInfo } from '@/client/types.gen'
import { clearMembershipRightsCache } from '@/hooks/useMembershipRights'
import { type LogoutReason, resetLogoutOverlayTriggered } from '@/services/initClient'
import { isSentrySendPiiEnabled } from '@/services/sentry'
import { isPublicAuthPage } from '@/utils/publicAuthPath'

interface AuthContextType {
	user: UserInfo | null
	loading: boolean
	login: (user: UserInfo) => void
	logout: () => void
	checkAuth: () => Promise<void>
	showLogoutOverlay: boolean
	logoutReason: LogoutReason | null
	triggerLogoutOverlay: (reason: LogoutReason) => void
	dismissLogoutOverlay: () => void
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

interface AuthProviderProps {
	children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
	const [user, setUser] = useState<UserInfo | null>(null)
	const [loading, setLoading] = useState(true)
	const [showLogoutOverlay, setShowLogoutOverlay] = useState(false)
	const [logoutReason, setLogoutReason] = useState<LogoutReason | null>(null)

	const checkAuth = useCallback(async () => {
		try {
			const response = await getCurrentUserInfo()
			if (response.data) {
				setUser(response.data)
			} else {
				setUser(null)
			}
		} catch (_error) {
			setUser(null)
			clearMembershipRightsCache()
		} finally {
			setLoading(false)
		}
	}, [])

	const login = useCallback((userData: UserInfo) => {
		resetLogoutOverlayTriggered()
		setUser(userData)
	}, [])

	const logout = useCallback(async () => {
		let kindeLogoutUrl: string | undefined
		try {
			const response = await logoutAPI()
			kindeLogoutUrl = response.data?.kinde_logout_url ?? undefined
		} catch (error) {
			console.error('Logout error:', error)
			// Continue with logout even if request fails
		} finally {
			setUser(null)
			clearMembershipRightsCache()
		}
		if (kindeLogoutUrl) {
			window.location.assign(kindeLogoutUrl)
		}
	}, [])

	const triggerLogoutOverlay = useCallback((reason: LogoutReason) => {
		clearMembershipRightsCache()
		setUser(null)
		setLogoutReason(reason)
		setShowLogoutOverlay(true)
	}, [])

	const dismissLogoutOverlay = useCallback(() => {
		setShowLogoutOverlay(false)
		setLogoutReason(null)
	}, [])

	useEffect(() => {
		if (isPublicAuthPage()) {
			setLoading(false)
			return
		}
		checkAuth()
	}, [checkAuth])

	// Sync logged-in user to Sentry. When PII is enabled, pre-fill feedback form from /me; otherwise only set id so users can optionally add email/name.
	useEffect(() => {
		if (!user?.id || !user?.email) {
			Sentry.setUser(null)
			return
		}

		const sendPii = isSentrySendPiiEnabled()
		if (!sendPii) {
			Sentry.setUser({ id: user.id })
			return
		}

		const fullName = `${user.firstname ?? ''} ${user.lastname ?? ''}`.trim()
		Sentry.setUser({
			id: user.id,
			email: user.email,
			username: fullName || user.email,
		})
	}, [user?.id, user?.email, user?.firstname, user?.lastname])

	// Listen for logout-required events from initClient.ts
	useEffect(() => {
		const handleLogoutRequired = (event: CustomEvent<LogoutReason>) => {
			triggerLogoutOverlay(event.detail)
		}

		window.addEventListener('logout-required', handleLogoutRequired as EventListener)

		return () => {
			window.removeEventListener('logout-required', handleLogoutRequired as EventListener)
		}
	}, [triggerLogoutOverlay])

	const value = useMemo(
		() => ({
			user,
			loading,
			login,
			logout,
			checkAuth,
			showLogoutOverlay,
			logoutReason,
			triggerLogoutOverlay,
			dismissLogoutOverlay,
		}),
		[
			user,
			loading,
			login,
			logout,
			checkAuth,
			showLogoutOverlay,
			logoutReason,
			triggerLogoutOverlay,
			dismissLogoutOverlay,
		]
	)

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth() {
	const context = useContext(AuthContext)
	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider')
	}
	return context
}
